import { Tab } from "@headlessui/react";
import {
  CogIcon,
  ViewListIcon,
  ClipboardListIcon,
} from "@heroicons/react/outline";
import clsx from "clsx";
import { Container } from "./Container";
import { getTranslation } from "dictionary";
import { useUserContext } from "context/UserContext";

function Feature({ feature, isActive, className, ...props }) {
  return (
    <div
      className={clsx(className, !isActive && "opacity-75 hover:opacity-100")}
      {...props}
    >
      <div
        className={clsx(
          "w-9 h-9 rounded-lg flex justify-center items-center",
          isActive ? "bg-secondary" : "bg-slate-500"
        )}
      >
        <div aria-hidden="true" className="h-7 w-7 text-white" fill="none">
          {feature.icon}
        </div>
      </div>
      <h3
        className={clsx(
          "mt-6 text-sm font-medium",
          isActive ? "text-secondary" : "text-slate-600"
        )}
      >
        {feature.name}
      </h3>
      <p className="mt-2 font-display text-xl text-slate-900">
        {feature.summary}
      </p>
    </div>
  );
}

function FeaturesMobile({ features }) {
  return (
    <div className="-mx-4 mt-20 flex flex-col gap-y-10 overflow-hidden px-4 sm:-mx-6 sm:px-6 lg:hidden">
      {features.map((feature) => (
        <div key={feature.name}>
          <Feature feature={feature} className="mx-auto max-w-2xl" isActive />
          <div className="relative mt-10 pb-10">
            <div className="absolute -inset-x-4 bottom-0 top-8 bg-slate-200 sm:-inset-x-6" />
            <div className="relative mx-auto w-[52.75rem] overflow-hidden rounded-xl bg-white shadow-lg shadow-slate-900/5 ring-1 ring-slate-500/10">
              <img
                className="w-full"
                src={feature.image}
                alt={feature.name}
                height="464"
                width="844"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FeaturesDesktop({ features }) {
  return (
    <Tab.Group as="div" className="hidden lg:mt-20 lg:block">
      {({ selectedIndex }) => (
        <>
          <Tab.List className="grid grid-cols-3 gap-x-8">
            {features.map((feature, featureIndex) => (
              <Feature
                key={feature.name}
                feature={{
                  ...feature,
                  name: (
                    <Tab className="focus:outline-none">
                      <span className="absolute inset-0" />
                      {feature.name}
                    </Tab>
                  ),
                }}
                isActive={featureIndex === selectedIndex}
                className="relative"
              />
            ))}
          </Tab.List>
          <Tab.Panels className="relative mt-20  overflow-hidden  bg-slate-200 px-14 py-16 xl:px-16">
            <div className="-mx-5 flex">
              {features.map((feature, featureIndex) => (
                <Tab.Panel
                  static
                  key={feature.name}
                  className={clsx(
                    "px-5 transition duration-500 ease-in-out [&:not(:focus-visible)]:focus:outline-none",
                    featureIndex !== selectedIndex && "opacity-60"
                  )}
                  style={{ transform: `translateX(-${selectedIndex * 100}%)` }}
                  aria-hidden={featureIndex !== selectedIndex}
                >
                  <div className="w-[52.75rem] overflow-hidden rounded-xl bg-white shadow-lg shadow-slate-900/5 ring-1 ring-slate-500/10">
                    <img
                      className="w-full"
                      src={feature.image}
                      alt={feature.name}
                      height="464"
                      width="844"
                    />
                  </div>
                </Tab.Panel>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-0 rounded-4xl ring-1 ring-inset ring-slate-900/10" />
          </Tab.Panels>
        </>
      )}
    </Tab.Group>
  );
}

export function SecondaryFeatures() {
  const { language } = useUserContext();
  const features = [
    {
      name: getTranslation("landingpage", "ACCOUNT_TITLE", language),
      summary: getTranslation("landingpage", "ACCOUNT_SUBTITLE", language),
      image: "images/screenshots/dashboard-account.png",
      icon: <CogIcon />,
    },
    {
      name: getTranslation("landingpage", "CATEGORIES_TITLE", language),
      summary: getTranslation("landingpage", "CATEGORIES_SUBTITLE", language),
      image: "images/screenshots/dashboard-categories.png",
      icon: <ViewListIcon />,
    },
    {
      name: getTranslation("landingpage", "PRODUCTS_TITLE", language),
      summary: getTranslation("landingpage", "PRODUCTS_SUBTITLE", language),
      image: "images/screenshots/dashboard-products.png",
      icon: <ClipboardListIcon />,
    },
  ];
  return (
    <section
      id="secondary-features"
      aria-label="Features for simplifying everyday business tasks"
      className="pt-20 pb-14 sm:pb-20 sm:pt-32 lg:pb-32"
    >
      <Container>
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl">
            {getTranslation("landingpage", "DASHBOARD_TITLE", language)}
          </h2>
          <p className="mt-4 text-xl tracking-tight text-slate-700 font-light">
            {getTranslation("landingpage", "DASHBOARD_SUBTITLE", language)}
          </p>
        </div>
        <FeaturesMobile features={features} />
        <FeaturesDesktop features={features} />
      </Container>
    </section>
  );
}
